import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { BotaoAcao } from '../../components/BotaoAcao'
import { StatTile } from '../../components/charts'
import { FilterBar, FilterSelect } from '../../components/filters'
import { IconCampaign, IconSearch } from '../../components/icons'
import { Modal } from '../../components/Modal'
import { t, tRico } from '../../lib/i18n'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/campanhas')({ component: Campaigns })

/** Mesmos períodos de Relatórios — dia-calendário de Brasília, ver `period.ts`. */
const PERIODOS = [
  { key: 'hoje' as const, label: 'Hoje' },
  { key: 'ontem' as const, label: 'Ontem' },
  { key: '7d' as const, label: '7 dias' },
  { key: '30d' as const, label: '30 dias' },
  { key: '90d' as const, label: '90 dias' },
]

type PeriodoKey = (typeof PERIODOS)[number]['key']

/**
 * A tela é uma leitura de desempenho, não um formulário: com várias campanhas
 * em várias contas, a pergunta do dia a dia é "qual traz mais?". Por isso o
 * agrupamento por conta, a barra comparativa e o período — e o cadastro,
 * que se usa uma vez por campanha, mora recolhido atrás de um botão.
 */
function Campaigns() {
  const trpc = useTRPC()
  const channels = useQuery(trpc.channel.list.queryOptions())
  const campaigns = useQuery(trpc.campaign.list.queryOptions({}))
  const queryClient = useQueryClient()

  /** Qual campanha está no segundo passo da confirmação. */
  const [confirmando, setConfirmando] = useState<string | null>(null)
  const apagar = useMutation({
    ...trpc.campaign.remove.mutationOptions(),
    onSuccess: () => {
      setConfirmando(null)
      queryClient.invalidateQueries({ queryKey: [['campaign']] })
    },
  })

  const [period, setPeriod] = useState<PeriodoKey>('30d')
  const stats = useQuery(trpc.report.campaigns.queryOptions({ period }))

  // Só por escolha. Antes abria sozinho quando não havia campanha nenhuma —
  // em janela isso viraria um formulário na cara de quem só abriu a tela.
  const [formOpen, setFormOpen] = useState(false)

  const [filtroConta, setFiltroConta] = useState<string | null>(null)
  const [filtroPrecisao, setFiltroPrecisao] = useState<'exact' | 'inferred' | null>(null)
  // Busca no cliente: a lista de campanhas é pequena e já está toda em mão —
  // uma ida ao servidor por tecla digitada não pagaria nada aqui.
  const [busca, setBusca] = useState('')

  // Cruza cadastro (link, precisão) com desempenho (leads no período).
  const desempenho = new Map((stats.data ?? []).map((row) => [row.campaignId, row]))
  const linhas = (campaigns.data ?? [])
    .map((row) => ({
      ...row,
      total: desempenho.get(row.id)?.total ?? 0,
      exatas: desempenho.get(row.id)?.exatas ?? 0,
      concluidas: desempenho.get(row.id)?.concluidas ?? 0,
      jaExistentes: desempenho.get(row.id)?.jaExistentes ?? 0,
    }))
    .map((row) => ({
      // O número grande é todo mundo que chegou pelo link: o lead novo e o que
      // já era conhecido. `total` sozinho escondia metade do trabalho que a
      // campanha deu — quem voltou por ela também veio por ela.
      ...row,
      alcance: row.total + row.jaExistentes,
    }))
    .filter((row) => !filtroConta || row.channelName === filtroConta)
    .filter((row) => !filtroPrecisao || row.precision === filtroPrecisao)
    .filter((row) => {
      const termo = busca.trim().toLowerCase()
      if (!termo) return true
      return `${row.label} ${row.key}`.toLowerCase().includes(termo)
    })

  const contas = [...new Set((campaigns.data ?? []).map((row) => row.channelName))].sort()
  const corDaConta = new Map((channels.data ?? []).map((row) => [row.id, row.color]))

  // Grupos por conta, ordenados por volume — a conta que mais traz vem antes.
  const grupos = [...new Map(linhas.map((row) => [row.channelName, row.channelName])).keys()]
    .map((nome) => {
      const rows = linhas
        .filter((row) => row.channelName === nome)
        .sort((a, b) => b.alcance - a.alcance)
      return { nome, rows, total: rows.reduce((sum, row) => sum + row.alcance, 0) }
    })
    .sort((a, b) => b.total - a.total)

  // Barra comparável entre todas as campanhas, não só dentro do grupo.
  const maiorTotal = Math.max(1, ...linhas.map((row) => row.alcance))

  const totalNovos = linhas.reduce((sum, row) => sum + row.total, 0)
  const totalJaExistentes = linhas.reduce((sum, row) => sum + row.jaExistentes, 0)
  const totalPeriodo = totalNovos + totalJaExistentes
  const totalExatas = linhas.reduce((sum, row) => sum + row.exatas, 0)
  const totalConcluidas = linhas.reduce((sum, row) => sum + row.concluidas, 0)

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-4xl px-5 pt-4 md:pt-8">
        {/* Título e botão numa linha só, descrição embaixo. Com os três no
            mesmo flex, no celular a descrição empurrava o botão para a linha
            de baixo — e ele acabava solto embaixo de um parágrafo. */}
        <div className="flex items-center gap-3">
          <h1 className="min-w-0 font-display text-2xl text-ink">{t('Campanhas')}</h1>
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="ml-auto shrink-0 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-ink"
          >
            {t('Nova campanha')}
          </button>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-2">
          {t(
            'Cada campanha gera um link rastreável. Quando alguém chega por ele, a origem fica registrada no perfil do lead.',
          )}
        </p>

        {formOpen && (
          <Modal
            title={t('Nova campanha')}
            description={t(
              'O link gerado leva direto para a conta escolhida e registra a origem no perfil de quem chegar por ele.',
            )}
            onClose={() => setFormOpen(false)}
          >
            <CampaignForm channels={channels.data ?? []} onCreated={() => setFormOpen(false)} />
          </Modal>
        )}

        {!campaigns.isPending && (campaigns.data ?? []).length === 0 && (
          <p className="mt-6 rounded-lg border border-dashed border-line-strong px-4 py-8 text-center text-xs text-ink-3">
            {t('Nenhuma campanha ainda. Crie a primeira em “Nova campanha”.')}
          </p>
        )}

        {(campaigns.data ?? []).length > 0 && (
          <>
            <div className="mt-6 flex flex-wrap gap-1">
              {PERIODOS.map((opcao) => (
                <button
                  key={opcao.key}
                  type="button"
                  onClick={() => setPeriod(opcao.key)}
                  aria-pressed={period === opcao.key}
                  className="rounded-full border border-line bg-surface px-2.5 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-2 aria-[pressed=true]:border-ink aria-[pressed=true]:bg-ink aria-[pressed=true]:text-paper"
                >
                  {t(opcao.label)}
                </button>
              ))}
            </div>

            {/* Os números primeiro, o filtro depois: quem abre a página quer
                saber quanto veio, e só então recortar. */}
            <section className="mt-4 grid gap-3 sm:grid-cols-4">
              <StatTile
                label={t('Leads no período')}
                value={stats.isPending ? '—' : totalPeriodo}
                hint={t('Novos e já existentes')}
              />
              <StatTile
                label={t('Novos')}
                value={stats.isPending ? '—' : totalNovos}
                hint={t('Primeiro contato veio por campanha')}
              />
              <StatTile
                label={t('Atribuição exata')}
                value={stats.isPending ? '—' : totalExatas}
                hint={t('Chegaram pelo link de /start')}
              />
              <StatTile
                label={t('Concluídos')}
                value={stats.isPending ? '—' : totalConcluidas}
                hint={t('Conversas em etapa final')}
              />
            </section>

            <label className="relative mt-4 flex items-center">
              <span className="sr-only">{t('Buscar campanha')}</span>
              <IconSearch className="pointer-events-none absolute left-2.5 size-[15px] text-ink-3" />
              <input
                type="search"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder={t('Buscar campanha pelo nome ou identificador')}
                className="w-full rounded-md border border-line bg-surface-2 py-2 pl-8 pr-3 text-sm placeholder:text-ink-3 hover:border-line-strong focus-visible:bg-surface"
              />
            </label>

            <FilterBar
              active={(filtroConta ? 1 : 0) + (filtroPrecisao ? 1 : 0)}
              onClear={() => {
                setFiltroConta(null)
                setFiltroPrecisao(null)
              }}
            >
              <FilterSelect
                label={t('Conta')}
                placeholder={t('Todas')}
                options={contas.map((nome) => ({ value: nome, label: nome }))}
                values={filtroConta ? [filtroConta] : []}
                onChange={([valor]) => setFiltroConta(valor ?? null)}
              />

              <FilterSelect
                label={t('Atribuição')}
                placeholder={t('Qualquer')}
                options={[
                  { value: 'exact', label: 'Exata' },
                  { value: 'inferred', label: 'Aproximada' },
                ]}
                values={filtroPrecisao ? [filtroPrecisao] : []}
                onChange={([valor]) => setFiltroPrecisao((valor as 'exact' | 'inferred') ?? null)}
              />
            </FilterBar>
          </>
        )}

        {linhas.length === 0 && (campaigns.data ?? []).length > 0 && (
          <p className="mt-6 rounded-lg border border-dashed border-line px-4 py-8 text-center text-xs text-ink-3">
            {t('Nenhuma campanha com esses filtros.')}
          </p>
        )}

        {grupos.map((grupo) => (
          <section key={grupo.nome} className="mt-6">
            <h2 className="flex flex-wrap items-baseline gap-2 text-sm font-semibold text-ink">
              <span
                aria-hidden
                className="size-2 self-center rounded-full"
                style={{
                  background:
                    corDaConta.get(grupo.rows[0]?.channelId ?? '') ?? 'var(--line-strong)',
                }}
              />
              {grupo.nome}
              <span className="text-2xs font-normal text-ink-3">
                {grupo.total === 1
                  ? t('{n} lead no período', { n: grupo.total })
                  : t('{n} leads no período', { n: grupo.total })}
              </span>
            </h2>

            <ul className="mt-2 space-y-2">
              {grupo.rows.map((campaign) => (
                <li key={campaign.id} className="rounded-lg border border-line bg-surface p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <IconCampaign className="size-4 flex-none text-ink-3" />
                    <span className="text-sm font-semibold text-ink">{campaign.label}</span>
                    <span
                      className="rounded-full px-2 py-0.5 text-2xs"
                      style={
                        campaign.precision === 'exact'
                          ? { color: 'var(--st-resolv)', background: 'var(--st-resolv-soft)' }
                          : { color: 'var(--st-atend)', background: 'var(--st-atend-soft)' }
                      }
                      title={
                        campaign.precision === 'exact'
                          ? t('O identificador viaja invisível no /start — atribuição exata')
                          : t('Atribuição inferida pelo texto da primeira mensagem')
                      }
                    >
                      {campaign.precision === 'exact' ? t('Exata') : t('Aproximada')}
                    </span>
                    {!campaign.isActive && (
                      <span className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs text-ink-3">
                        {t('Inativa')}
                      </span>
                    )}

                    <span
                      className="ml-auto text-xs text-ink-2"
                      title={t(
                        'Todo mundo que chegou por esta campanha: os novos e os que já estavam no CRM',
                      )}
                    >
                      {tRico(campaign.alcance === 1 ? '{n} lead' : '{n} leads', {
                        n: (
                          <strong className="text-md font-bold tabular-nums text-ink">
                            {campaign.alcance}
                          </strong>
                        ),
                      })}
                    </span>
                  </div>

                  {/* Barra na mesma escala em todos os grupos: dá para varrer
                      a página e ver qual campanha puxa mais, sem ler número. */}
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.max((campaign.alcance / maiorTotal) * 100, campaign.alcance > 0 ? 2 : 0)}%`,
                        background: 'var(--chart-in)',
                      }}
                    />
                  </div>

                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-ink-3">
                    {/* Novos e já existentes lado a lado: juntos são o número
                        grande acima, e separados contam a diferença entre
                        trazer gente nova e trazer gente de volta. */}
                    <span
                      className="text-ink-2"
                      title={t(
                        'Leads cuja origem registrada é esta campanha — o primeiro contato deles veio por ela',
                      )}
                    >
                      {campaign.total === 1
                        ? t('{n} novo', { n: campaign.total })
                        : t('{n} novos', { n: campaign.total })}
                    </span>
                    <span
                      className="text-ink-2"
                      title={t(
                        'Chegaram por esta campanha já estando no CRM. A origem registrada continua sendo a primeira.',
                      )}
                    >
                      {campaign.jaExistentes === 1
                        ? t('{n} já existente', { n: campaign.jaExistentes })
                        : t('{n} já existentes', { n: campaign.jaExistentes })}
                    </span>
                    <span>
                      {campaign.exatas === 1
                        ? t('{n} exato', { n: campaign.exatas })
                        : t('{n} exatos', { n: campaign.exatas })}
                    </span>
                    <span>
                      {campaign.concluidas === 1
                        ? t('{n} concluído', { n: campaign.concluidas })
                        : t('{n} concluídos', { n: campaign.concluidas })}
                    </span>
                  </div>

                  {campaign.link ? (
                    <div className="mt-2 flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 text-2xs text-ink-2">
                        {campaign.link}
                      </code>
                      <CopyLink link={campaign.link} />
                      {/* Em dois passos, sem digitar o nome: aqui o histórico
                          de atendimento não some — só a atribuição de origem
                          dos leads que vieram por ela. É perda real, mas de
                          outra ordem que apagar uma conta inteira. */}
                      <BotaoAcao
                        variante="destrutiva"
                        disabled={apagar.isPending}
                        onBlur={() => setConfirmando(null)}
                        onClick={() =>
                          confirmando === campaign.id
                            ? apagar.mutate({ campaignId: campaign.id })
                            : setConfirmando(campaign.id)
                        }
                        title={
                          campaign.alcance > 0
                            ? t('{n} lead(s) perdem a origem registrada', {
                                n: campaign.alcance,
                              })
                            : t('Nenhum lead veio por esta campanha')
                        }
                      >
                        {confirmando === campaign.id
                          ? campaign.alcance > 0
                            ? t('Apagar e soltar {n} lead(s)?', { n: campaign.alcance })
                            : t('Confirmar')
                          : t('Apagar')}
                      </BotaoAcao>
                    </div>
                  ) : (
                    <p className="mt-2 text-2xs text-danger">{campaign.linkError}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}

        {(campaigns.data ?? []).length === 0 && !campaigns.isPending && (
          <p className="mt-6 rounded-lg border border-dashed border-line px-4 py-8 text-center text-xs text-ink-3">
            {t('Nenhuma campanha ainda — crie a primeira acima.')}
          </p>
        )}
      </div>
    </div>
  )
}

function CopyLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(link)
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      }}
      className="shrink-0 rounded border border-line px-2 py-1 text-2xs text-ink-2 hover:bg-surface-2"
    >
      {copied ? t('Copiado') : t('Copiar')}
    </button>
  )
}

interface ChannelOption {
  id: string
  name: string
  type: string
}

function CampaignForm({
  channels,
  onCreated,
}: {
  channels: ChannelOption[]
  onCreated: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [channelId, setChannelId] = useState('')
  const [key, setKey] = useState('')
  const [label, setLabel] = useState('')
  const [prefillText, setPrefillText] = useState('')

  const create = useMutation({
    ...trpc.campaign.create.mutationOptions(),
    onSuccess: () => {
      setKey('')
      setLabel('')
      setPrefillText('')
      queryClient.invalidateQueries({ queryKey: [['campaign']] })
      queryClient.invalidateQueries({ queryKey: [['report']] })
      onCreated()
    },
  })

  const selected = channels.find((channel) => channel.id === channelId)
  const isBusiness = selected?.type === 'business'

  function submit(event: FormEvent) {
    event.preventDefault()
    create.mutate({
      channelId,
      key,
      label,
      ...(prefillText ? { prefillText } : {}),
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-4">
      <Field label={t('Conta')}>
        <select
          value={channelId}
          onChange={(e) => setChannelId(e.target.value)}
          required
          className="field"
        >
          <option value="">{t('Selecione…')}</option>
          {channels.map((channel) => (
            <option key={channel.id} value={channel.id}>
              {channel.name}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('Nome')} hint={t('Como aparece no CRM')}>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            placeholder={t('ODD 200')}
            className="field"
          />
        </Field>
        <Field label={t('Identificador')} hint={t('Letras, números, _ e - (até 64)')}>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
            pattern="[A-Za-z0-9_-]{1,64}"
            placeholder={t('odd200')}
            className="field"
          />
        </Field>
      </div>

      {/* Conta business não aceita payload de /start: o link pré-preenche
          a primeira mensagem e a atribuição é por texto. */}
      {isBusiness && (
        <Field
          label={t('Texto pré-preenchido')}
          hint={t(
            'O cliente vê este texto no campo de mensagem — e pode editá-lo antes de enviar, então a atribuição é aproximada.',
          )}
        >
          <input
            value={prefillText}
            onChange={(e) => setPrefillText(e.target.value)}
            required
            placeholder={t('Quero saber da ODD 200')}
            className="field"
          />
        </Field>
      )}

      {create.error && (
        <p role="alert" className="text-xs text-danger">
          {t(create.error.message)}
        </p>
      )}

      <button
        type="submit"
        disabled={create.isPending || !channelId}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-on-accent disabled:opacity-50"
      >
        {create.isPending ? t('Criando…') : t('Criar campanha')}
      </button>

      <style>{`
        .field {
          width: 100%;
          border-radius: 8px;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          padding: 7px 10px;
          font-size: 13px;
          color: var(--ink);
        }
      `}</style>
    </form>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-ink-3">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-2xs leading-relaxed text-ink-3">{hint}</span>}
    </label>
  )
}
